import React, { useEffect, useRef, useState } from 'react';
import { gradeApi } from '../../../../api.js';
import { useAuth } from '../../../../auth/AuthContext';
import { useParams } from 'react-router-dom';

const StudentSearchDropdown = ({
  students,
  currentStudentName,
  onStudentSelect,
  assignmentId
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [studentList, setStudentList] = useState([]);
  const [submissionStatus, setSubmissionStatus] = useState({});
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);
  const { user } = useAuth();
  const params = useParams();

  // Get assignment ID from props or URL
  const effectiveAssignmentId = assignmentId || params.assignment_id;

  // Fetch submission status when dropdown opens
  useEffect(() => {
    if (!isOpen || !effectiveAssignmentId || !user) {return;}
    
    const fetchSubmissions = async () => {
      setLoading(true);
      
      try {
        // Use the v2 submissions endpoint to get submission status
        const response = await gradeApi.get({
          path: `/api/v2/assignments/${effectiveAssignmentId}/submissions?offset=0&limit=2000&sortOrder=asc`,
          config: {
            headers: {
              Authorization: `Bearer ${user.session_token}`
            }
          }
        });
        
        console.log('Submissions data:', response.data);
        
        // Create map of student IDs to submission status
        const statusMap = {};
        
        // If we have entities in the response, use them for both statusMap and studentList
        if (response.data && response.data.entities && Array.isArray(response.data.entities)) {
          console.log('Got entities from API:', response.data.entities.length);
          
          // Create status map
          response.data.entities.forEach(entity => {
            if (entity.studentId) {
              statusMap[entity.studentId] = {
                status: entity.status,
                submission_date: entity.submission_date
              };
            }
          });
          
          setSubmissionStatus(statusMap);
          
          // Use entities as student list (filter out 'Unmatched Submission' entries)
          const filteredEntities = response.data.entities.filter(entity => 
            entity.first_name !== 'Unmatched Submission'
          );
          
          console.log('Using filtered entities for student list:', filteredEntities.length);
          setStudentList(filteredEntities);
          return; // Exit early as we've set the student list
        }
        
        // Fallback to students from props if available
        if (students) {
          // Handle both array format and {entities: [...]} format
          if (Array.isArray(students)) {
            console.log('Using students array from props:', students.length);
            setStudentList(students);
          } else if (students.entities && Array.isArray(students.entities)) {
            console.log('Using students.entities from props:', students.entities.length);
            setStudentList(students.entities);
          } else {
            console.warn('Students prop is in an unexpected format:', students);
          }
        } else {
          console.warn('No student data available from API or props');
        }
      } catch (error) {
        console.error('Error fetching submissions:', error);
        if (students && Array.isArray(students)) {
          setStudentList(students);
        }
      } finally {
        setLoading(false);
      }
    };
    
    fetchSubmissions();
  }, [isOpen, effectiveAssignmentId, user, students]);

  // Filter students by search text
  const filteredStudents = studentList.filter(student => {
    // Ensure we have a valid student object
    if (!student) {return false;}
    
    // Skip unmatched submissions
    if (student.first_name === 'Unmatched Submission') {return false;}
    
    // If no search text, include all students
    if (!searchText) {return true;}
    
    // Search by name
    const fullName = student.full_name || `${student.first_name || ''} ${student.last_name || ''}`;
    return fullName.toLowerCase().includes(searchText.toLowerCase());
  })
  // Sort students - those without submissions first, then those with submissions
  .sort((a, b) => {
    // Helper function to check if a student has submissions
    const hasSubmission = (student) => {
      return (
        (submissionStatus[student.id] && submissionStatus[student.id].status !== 'Not submitted') || 
        (submissionStatus[student.studentId] && submissionStatus[student.studentId].status !== 'Not submitted')
      );
    };
    
    const aHasSubmission = hasSubmission(a);
    const bHasSubmission = hasSubmission(b);
    
    // If one has submission and the other doesn't, sort accordingly
    if (aHasSubmission && !bHasSubmission) {
      return 1; // a has submission, move to end
    }
    if (!aHasSubmission && bHasSubmission) {
      return -1; // b has submission, move to end
    }
    
    // If both have same submission status, sort alphabetically by name
    const aName = a.full_name || `${a.first_name || ''} ${a.last_name || ''}`;
    const bName = b.full_name || `${b.first_name || ''} ${b.last_name || ''}`;
    return aName.localeCompare(bName);
  });

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus the search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      // Small timeout to ensure the dropdown is rendered
      setTimeout(() => {
        searchInputRef.current.focus();
      }, 50);
    }
  }, [isOpen]);

  return (
    <div className="relative" ref={dropdownRef}>
      <div 
        className="flex items-center justify-between border border-gray-300 rounded-md p-2 cursor-pointer"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div>{currentStudentName || 'Select a student'}</div>
        <div>
          <svg className={`h-5 w-5 transform ${isOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>
      
      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-1 z-10 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
          <div className="p-2">
            <input
              type="text"
              className="w-full border border-gray-300 rounded-md p-2"
              placeholder="Search students..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              ref={searchInputRef}
            />
          </div>
          
          {loading ? (
            <div className="p-4 text-center">Loading...</div>
          ) : filteredStudents.length > 0 ? (
            <ul>
              {filteredStudents.map((student) => (
                <li
                  key={student.id || student.studentId}
                  className="p-2 hover:bg-gray-100 cursor-pointer"
                  onClick={() => {
                    // Make sure we pass a student object with the correct ID format
                    // TDetails.js expects student.id to be the student ID
                    const normalizedStudent = {
                      ...student,
                      id: student.studentId || student.id  // Use studentId as primary ID if available
                    };
                    console.log('Selected student with ID:', normalizedStudent.id);
                    
                    // Check if student has a submission
                    const hasSubmission = (
                      (submissionStatus[student.id] && submissionStatus[student.id].status !== 'Not submitted') || 
                      (submissionStatus[student.studentId] && submissionStatus[student.studentId].status !== 'Not submitted')
                    );
                    
                    // Pass both the student and whether they have a submission to parent
                    onStudentSelect(normalizedStudent, hasSubmission);
                    setIsOpen(false);
                  }}
                >
                  <div>{student.full_name || `${student.first_name || ''} ${student.last_name || ''}`}</div>
                  {/* Check if student has a submission with status not 'Not submitted' */}
                  {(
                    (submissionStatus[student.id] && submissionStatus[student.id].status !== 'Not submitted') || 
                    (submissionStatus[student.studentId] && submissionStatus[student.studentId].status !== 'Not submitted')
                  ) && (
                    <div 
                      className="text-sm font-medium px-2 py-0.5 rounded-md inline-flex items-center" 
                      style={{
                        backgroundColor: 'rgba(228, 177, 122, 0.2)',
                        color: '#c99b69',
                        border: '1px solid rgba(228, 177, 122, 0.3)'
                      }}
                    >
                      <svg 
                        xmlns="http://www.w3.org/2000/svg" 
                        viewBox="0 0 20 20" 
                        fill="currentColor" 
                        className="w-3.5 h-3.5 mr-1"
                      >
                        <path 
                          fillRule="evenodd" 
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z" 
                          clipRule="evenodd" 
                        />
                      </svg>
                      Has submission
                    </div>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <div className="p-4 text-center">No students found</div>
          )}
        </div>
      )}
    </div>
  );
};

export default StudentSearchDropdown;
